import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { AppDispatch, RootState } from "@app/store";
import { fetchFilledFormById, updateFilledForm } from "./filledFormSlice";
import {
  Box,
  Typography,
  Paper,
  Divider,
  CircularProgress,
  Chip,
  TextField,
  Button,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
} from "@mui/material";
import { PhaseFormFieldType } from "@features/phaseForm/types";
import EventIcon from "@mui/icons-material/Event";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import PersonIcon from "@mui/icons-material/Person";
import EmailIcon from "@mui/icons-material/Email";
import PhoneIcon from "@mui/icons-material/Phone";
import NoteIcon from "@mui/icons-material/Note";
import DownloadIcon from "@mui/icons-material/Download";
import FileIcon from "@mui/icons-material/InsertDriveFile";
import { UploadField, UploadFile } from "../formWizard/components/UploadField";
import { API_URL } from "@/config/api";
import { filledFormService } from "./filledFormService";

interface FormField {
  _id: string;
  type: PhaseFormFieldType;
  name: string;
  description?: string;
  options?: Array<{ key: string; value: string }>;
  multiline?: boolean;
}

interface FormDataItem {
  field: FormField;
  data: any;
}

interface FilledFormFormProps {
  id?: string;
  hideHeader?: boolean;
  onSuccess?: () => void;
}

const FilledFormForm: React.FC<FilledFormFormProps> = ({
  id: propId,
  hideHeader = false,
  onSuccess,
}) => {
  const { id: paramId } = useParams<{ id: string }>();
  const id = propId || paramId;
  console.log("FilledFormForm ID:", id);
  const dispatch = useDispatch<AppDispatch>();
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [editedValues, setEditedValues] = useState<Record<string, any>>({});
  const [isSaving, setIsSaving] = useState(false);
  const [products, setProducts] = useState([]);

  const loadData = async () => {
    let keys = await (await fetch(API_URL + "/products/keys")).json();
    setProducts(keys);
  };
  // Buscar dados do formulário preenchido
  useEffect(() => {
    if (id) {
      setIsLoading(true);
      setError(null);
      loadData();
      dispatch(fetchFilledFormById(id))
        .unwrap()
        .then(() => {
          setIsLoading(false);
        })
        .catch((err) => {
          console.error("Erro ao carregar formulário:", err);
          setError("Não foi possível carregar os dados do formulário.");
          setIsLoading(false);
        });
    }
  }, [dispatch, id]);

  // Marca o filledForm como aberto (visualizado pelo advogado) — fire & forget.
  // Idempotente no BE: só seta openedAt na primeira chamada.
  useEffect(() => {
    if (!id) return;
    filledFormService.markOpened(id).catch((err) => {
      console.warn("mark-opened falhou (não bloqueia):", err);
    });
  }, [id]);

  // Obter dados do estado Redux
  const filledForm = useSelector(
    (state: RootState) => state.filledForm.currentItem
  );

  // Renderizar estado de carregamento
  if (isLoading) {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: "300px",
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  // Renderizar erro
  if (error || !filledForm) {
    return (
      <Box sx={{ p: 3 }}>
        <Typography color="error">
          {error || "Formulário não encontrado."}
        </Typography>
      </Box>
    );
  }

  const onlyUnique = (value: any, index: any, array: any) => {
    return array.indexOf(value) === index;
  };

  // Função para atualizar valor editado
  const handleValueChange = (fieldId: string, value: any, type: any = null) => {
    if (!fieldId) {
      console.warn("handleValueChange called with invalid fieldId");
      return;
    }
    if (type == PhaseFormFieldType.BINDING_QUESTION) {
      let unique = value.filter(onlyUnique);
    }

    setEditedValues((prev) => ({
      ...prev,
      [fieldId]: value,
    }));
  };

  // Função para salvar alterações
  const handleSave = async () => {
    if (!filledForm) {
      console.error("filledForm is not available");
      return;
    }

    setIsSaving(true);
    try {
      const updatedData =
        filledForm.data?.map((item: FormDataItem) => ({
          ...item,
          data:
            editedValues[item?.field?._id] !== undefined
              ? editedValues[item.field._id]
              : item.data,
        })) || [];

      if (!filledForm._id) {
        throw new Error("Form ID is missing");
      }

      await dispatch(
        updateFilledForm({
          id: filledForm._id,
          data: {
            ...filledForm,
            data: updatedData,
          },
        })
      ).unwrap();

      // Clear edited values after successful save
      setEditedValues({});
      if (onSuccess) {
        onSuccess();
      }
    } catch (err) {
      console.error("Erro ao salvar alterações:", err);
      setError(
        "Não foi possível salvar as alterações. Por favor, tente novamente."
      );
    } finally {
      setIsSaving(false);
    }
  };

  // Função para renderizar link de download
  const renderFileDownload = (file: any) => {
    if (!file || !file.path) return null;

    // Função para formatar o tamanho do arquivo
    const formatFileSize = (bytes: number) => {
      if (!bytes) return "";
      const sizes = ["Bytes", "KB", "MB", "GB"];
      const i = Math.floor(Math.log(bytes) / Math.log(1024));
      return `${(bytes / Math.pow(1024, i)).toFixed(2)} ${sizes[i]}`;
    };

    // Abre PDF via signed URL — bucket S3 e privado, link direto retorna 403.
    // POST /api/files/sign devolve URL temporaria valida por 1h.
    const handleOpenFile = async () => {
      try {
        const token = localStorage.getItem('token') || '';
        const resp = await fetch(API_URL + '/files/sign', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Authorization: token },
          body: JSON.stringify({ url: file.path }),
        });
        if (!resp.ok) {
          window.open(file.path, '_blank');
          return;
        }
        const data = await resp.json();
        if (data.signedUrl) {
          window.open(data.signedUrl, '_blank', 'noopener,noreferrer');
        } else {
          window.open(file.path, '_blank');
        }
        // Marca como baixado — idempotente, fire & forget.
        if (id) {
          filledFormService.markDownloaded(id).catch((err) => {
            console.warn('mark-downloaded falhou (não bloqueia):', err);
          });
        }
      } catch (err) {
        console.error('Erro gerando signedUrl, abrindo path direto:', err);
        window.open(file.path, '_blank');
      }
    };

    return (
      <Box
        key={file.id}
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 1,
          mb: 1,
          p: 1,
          borderRadius: 1,
          bgcolor: "background.paper",
          "&:hover": {
            bgcolor: "action.hover",
          },
        }}
      >
        <FileIcon color="primary" fontSize="small" />
        <Box sx={{ flexGrow: 1 }}>
          <Box
            component="span"
            onClick={handleOpenFile}
            sx={{
              display: "flex",
              alignItems: "center",
              color: "primary.main",
              textDecoration: "none",
              cursor: "pointer",
              "&:hover": {
                textDecoration: "underline",
              },
            }}
          >
            <Typography>{file.fileName}</Typography>
          </Box>
          {file.fileSize && (
            <Typography variant="caption" color="text.secondary">
              {formatFileSize(file.fileSize)}
            </Typography>
          )}
        </Box>
        <DownloadIcon
          color="primary"
          fontSize="small"
          sx={{ cursor: "pointer" }}
          onClick={handleOpenFile}
        />
      </Box>
    );
  };

  // Função simples para renderizar dados de pessoa
  interface PersonData {
    name?: string;
    email?: string;
    cpf?: string;
    cpfCnpj?: string;
    celphone?: string;
    whatsapp?: string;
    couple?: PersonData[];
    children?: PersonData[];
    legalRepresentatives?: PersonData[];
  }

  const renderPerson = (person: PersonData) => {
    if (!person) return null;

    // Se person for um array, renderizar cada item
    if (Array.isArray(person)) {
      return (
        <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
          {person.map((p, index) => (
            <Box
              key={index}
              sx={{
                p: 2,
                border: "1px solid",
                borderColor: "divider",
                borderRadius: 1,
                bgcolor: "background.paper",
              }}
            >
              <Typography variant="subtitle2" color="primary" sx={{ mb: 1 }}>
                Pessoa {index + 1}:
              </Typography>
              {renderPerson(p)}
            </Box>
          ))}
        </Box>
      );
    }

    // Se person for uma string, tentar fazer parse de JSON ou exibir como texto
    if (typeof person === "string") {
      try {
        const parsedPerson = JSON.parse(person);
        return renderPerson(parsedPerson);
      } catch {
        return <Typography>{person}</Typography>;
      }
    }

    // Se person não for um objeto, exibir como string
    if (typeof person !== "object") {
      return <Typography>{String(person)}</Typography>;
    }

    return (
      <Box sx={{ mb: 2 }}>
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" },
            gap: 2,
            p: 2,
            bgcolor: "background.paper",
            borderRadius: 1,
          }}
        >
          {person.name && (
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <PersonIcon color="primary" fontSize="small" />
              <Typography>
                <strong>Nome:</strong> {person.name}
              </Typography>
            </Box>
          )}

          {(person.cpf || person.cpfCnpj) && (
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <NoteIcon color="primary" fontSize="small" />
              <Typography>
                <strong>CPF/CNPJ:</strong> {person.cpf || person.cpfCnpj}
              </Typography>
            </Box>
          )}

          {person.email && (
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <EmailIcon color="primary" fontSize="small" />
              <Typography>
                <strong>Email:</strong> {person.email}
              </Typography>
            </Box>
          )}

          {(person.celphone || person.whatsapp) && (
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <PhoneIcon color="primary" fontSize="small" />
              <Typography>
                <strong>Telefone:</strong> {person.celphone || person.whatsapp}
              </Typography>
            </Box>
          )}
        </Box>

        {/* Renderizar cônjuge */}
        {person.couple && person.couple.length > 0 && (
          <Box
            sx={{
              mt: 2,
              ml: 4,
              p: 2,
              borderLeft: "2px dashed",
              borderColor: "primary.light",
            }}
          >
            <Typography
              variant="subtitle2"
              sx={{ fontWeight: "bold", color: "primary.main", mb: 1 }}
            >
              Cônjuge
            </Typography>
            {person.couple.map((spouse, index) => (
              <Box key={index}>{renderPerson(spouse)}</Box>
            ))}
          </Box>
        )}

        {/* Renderizar filhos */}
        {person.children && person.children.length > 0 && (
          <Box
            sx={{
              mt: 2,
              ml: 4,
              p: 2,
              borderLeft: "2px dashed",
              borderColor: "secondary.light",
            }}
          >
            <Typography
              variant="subtitle2"
              sx={{ fontWeight: "bold", color: "secondary.main", mb: 1 }}
            >
              Filhos
            </Typography>
            {person.children.map((child, index) => (
              <Box key={index} sx={{ mb: 2 }}>
                {renderPerson(child)}
              </Box>
            ))}
          </Box>
        )}

        {/* Renderizar representantes legais */}
        {person.legalRepresentatives &&
          person.legalRepresentatives.length > 0 && (
            <Box
              sx={{
                mt: 2,
                ml: 4,
                p: 2,
                borderLeft: "2px dashed",
                borderColor: "info.light",
              }}
            >
              <Typography
                variant="subtitle2"
                sx={{ fontWeight: "bold", color: "info.main", mb: 1 }}
              >
                Representantes Legais
              </Typography>
              {person.legalRepresentatives.map((representative, index) => (
                <Box key={index} sx={{ mb: 2 }}>
                  {renderPerson(representative)}
                </Box>
              ))}
            </Box>
          )}
      </Box>
    );
  };

  // Função utilitária para detectar e renderizar diferentes tipos de dados
  const renderDataValue = (
    data: any,
    fieldName: string = ""
  ): React.ReactNode => {
    if (data === null || data === undefined) {
      return <Typography color="text.secondary">Nenhum valor</Typography>;
    }

    // Tentar fazer parse de JSON se for uma string que parece JSON
    if (
      typeof data === "string" &&
      (data.startsWith("{") || data.startsWith("["))
    ) {
      try {
        const parsedData = JSON.parse(data);
        return renderDataValue(parsedData, fieldName);
      } catch {
        // Se não conseguir fazer parse, continuar com a string original
      }
    }

    // Se for um array
    if (Array.isArray(data)) {
      if (data.length === 0) {
        return <Typography color="text.secondary">Lista vazia</Typography>;
      }

      return (
        <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
          {data.map((item, index) => (
            <Box
              key={index}
              sx={{
                p: 1,
                border: "1px solid",
                borderColor: "divider",
                borderRadius: 1,
                bgcolor: "background.paper",
              }}
            >
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ display: "block", mb: 0.5 }}
              >
                Item {index + 1}:
              </Typography>
              {renderDataValue(item, `${fieldName}[${index}]`)}
            </Box>
          ))}
        </Box>
      );
    }

    // Se for um objeto
    if (typeof data === "object" && data !== null) {
      const keys = Object.keys(data);

      if (keys.length === 0) {
        return <Typography color="text.secondary">Objeto vazio</Typography>;
      }

      // Tratamento especial para dados de pessoa
      if (
        data.name ||
        data.email ||
        data.cpf ||
        data.cpfCnpj ||
        data.celphone ||
        data.whatsapp
      ) {
        return renderPerson(data);
      }

      // Tratamento especial para dados de arquivo
      if (data.path || data.fileName || data.fileSize) {
        return renderFileDownload(data);
      }

      // Tratamento especial para dados de bem (asset)
      if (data.assetType || data.description || data.value || data.quantity) {
        return (
          <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
            {data.assetType && (
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <Typography variant="subtitle2" color="primary">
                  Tipo:
                </Typography>
                <Typography>
                  {typeof data.assetType === "object" && data.assetType !== null
                    ? data.assetType.name
                    : data.assetType}
                </Typography>
              </Box>
            )}
            {data.description && (
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <Typography variant="subtitle2" color="primary">
                  Descrição:
                </Typography>
                <Typography>{data.description}</Typography>
              </Box>
            )}
            {data.value && (
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <Typography variant="subtitle2" color="primary">
                  Valor:
                </Typography>
                <Typography>
                  R${" "}
                  {Number(data.value).toLocaleString("pt-BR", {
                    minimumFractionDigits: 2,
                    maximumFractionDigits: 2,
                  })}
                </Typography>
              </Box>
            )}
            {data.quantity && (
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <Typography variant="subtitle2" color="primary">
                  Quantidade:
                </Typography>
                <Typography>{data.quantity}</Typography>
              </Box>
            )}
          </Box>
        );
      }

      // Tratamento especial para dados de papel (role)
      if (data.processRole || data.role || data.roleName) {
        return (
          <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
            {data.processRole && (
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <Typography variant="subtitle2" color="primary">
                  Papel:
                </Typography>
                <Typography>
                  {typeof data.processRole === "object" &&
                  data.processRole !== null
                    ? data.processRole.name
                    : data.processRole}
                </Typography>
              </Box>
            )}
            {data.role && (
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <Typography variant="subtitle2" color="primary">
                  Papel:
                </Typography>
                <Typography>
                  {typeof data.role === "object" && data.role !== null
                    ? data.role.name
                    : data.role}
                </Typography>
              </Box>
            )}
            {data.roleName && (
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <Typography variant="subtitle2" color="primary">
                  Papel:
                </Typography>
                <Typography>{data.roleName}</Typography>
              </Box>
            )}
          </Box>
        );
      }

      // Tratamento especial para datas
      if (data.date || (typeof data === "string" && !isNaN(Date.parse(data)))) {
        const dateValue = data.date || data;
        // Ensure dateValue is properly converted to string before passing to formatDateTime
        const { date, time } = formatDateTime(String(dateValue));
        return (
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <EventIcon color="primary" fontSize="small" />
            <Typography>
              {date} às {time}
            </Typography>
          </Box>
        );
      }

      return (
        <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
          {keys.map((key) => {
            const value = data[key];
            return (
              <Box
                key={key}
                sx={{
                  p: 1,
                  border: "1px solid",
                  borderColor: "divider",
                  borderRadius: 1,
                  bgcolor: "background.paper",
                }}
              >
                <Typography
                  variant="subtitle2"
                  color="primary"
                  sx={{ mb: 0.5 }}
                >
                  {key}:
                </Typography>
                {renderDataValue(value, `${fieldName}.${key}`)}
              </Box>
            );
          })}
        </Box>
      );
    }

    // Se for uma string
    if (typeof data === "string") {
      if (data.trim() === "") {
        return <Typography color="text.secondary">Texto vazio</Typography>;
      }

      // Verificar se é uma data válida (ISO string, timestamp, etc.)
      const datePatterns = [
        /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}/, // ISO format
        /^\d{4}-\d{2}-\d{2}$/, // Date only
        /^\d{2}\/\d{2}\/\d{4}$/, // DD/MM/YYYY
        /^\d{2}-\d{2}-\d{4}$/, // DD-MM-YYYY
      ];

      const isDate =
        datePatterns.some((pattern) => pattern.test(data)) ||
        !isNaN(Date.parse(data));

      if (isDate) {
        const { date, time } = formatDateTime(data);
        return (
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <EventIcon color="primary" fontSize="small" />
            <Typography>
              {date} às {time}
            </Typography>
          </Box>
        );
      }

      return <Typography>{data}</Typography>;
    }

    // Se for um número
    if (typeof data === "number") {
      // Verificar se é um valor monetário (baseado no nome do campo)
      const moneyKeywords = [
        "valor",
        "preço",
        "money",
        "custo",
        "taxa",
        "comissão",
        "pagamento",
        "recebimento",
      ];
      const isMoneyField = moneyKeywords.some((keyword) =>
        fieldName.toLowerCase().includes(keyword)
      );

      if (isMoneyField) {
        return (
          <Typography>
            R${" "}
            {data.toLocaleString("pt-BR", {
              minimumFractionDigits: 2,
              maximumFractionDigits: 2,
            })}
          </Typography>
        );
      }

      return <Typography>{data.toLocaleString()}</Typography>;
    }

    // Se for um booleano
    if (typeof data === "boolean") {
      return (
        <Chip
          label={data ? "Sim" : "Não"}
          color={data ? "success" : "default"}
          size="small"
        />
      );
    }

    // Para outros tipos, tentar converter para string
    return <Typography>{String(data)}</Typography>;
  };

  const renderDoc = (field) => {
    if (!field.path) {
      return (
        <Typography color="text.secondary">Nenhum arquivo enviado</Typography>
      );
    }

    return (
      <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
        {renderFileDownload(field)}
      </Box>
    );
  };

  // Função simples para renderizar o valor do campo com base no tipo
  const renderFieldValue = (field: any, data: any) => {
    if (!field) {
      return (
        <Typography color="text.secondary">Campo não encontrado</Typography>
      );
    }

    // Handle undefined data
    /*
    if ((data === undefined || data === null) && field.type !== PhaseFormFieldType.UPLOAD) {
      return (
        <Typography color="text.secondary">Nenhum dado disponível</Typography>
      );
    }
    */

    const editedValue = editedValues[field._id];

    switch (field.type) {
      case PhaseFormFieldType.PERSON:
        if (!data) return null;

        // Verificar se os dados estão aninhados em uma propriedade 'value'
        const personData = data.value || data;

        return (
          <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
            {field.description && (
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                {field.description}
              </Typography>
            )}

            {/* Renderizar dados de pessoa usando a função utilitária */}
            <Box sx={{ mb: 2 }}>
              <Typography variant="subtitle2" color="primary" gutterBottom>
                Dados da Pessoa:
              </Typography>
              {renderDataValue(personData, field.name)}
            </Box>

            {/* Renderizar arquivos anexados se existirem */}
            {data.files && data.files.length > 0 && (
              <Box sx={{ mt: 2 }}>
                <Typography variant="subtitle2" color="primary" gutterBottom>
                  Arquivos Anexados:
                </Typography>
                {data.files.map((filePath: string, index: number) => (
                  <Box key={index}>
                    {renderFileDownload({
                      path: filePath,
                      fileName:
                        filePath.split("/").pop() || `Arquivo ${index + 1}`,
                    })}
                  </Box>
                ))}
              </Box>
            )}
          </Box>
        );

      case PhaseFormFieldType.SELECT:
        // Encontrar a opção correspondente ao valor atual
        const currentOption = field.options?.find(
          (opt: any) => opt.value === data
        );
        const editedOption =
          editedValue !== undefined
            ? field.options?.find((opt: any) => opt.value === editedValue)
            : null;

        // Verificar se é múltipla seleção
        const isMultiline = field.multiline || false;

        if (isMultiline) {
          // Para múltipla seleção, data pode ser um array
          const selectedValues = Array.isArray(data)
            ? data
            : data
            ? [data]
            : [];
          const selectedEditedValues = [
            ...selectedValues,
            ...(Array.isArray(editedValue)
              ? editedValue
              : editedValue
              ? [editedValue]
              : []),
          ];

          const getSelectedLabels = (values: any[]) => {
            return values
              .map((value) => {
                const option = field.options?.find(
                  (opt: any) => opt.value === value
                );
                return option?.key || value;
              })
              .join(", ");
          };

          return (
            <Box>
              <Typography color="text.secondary" sx={{ mb: 1 }}>
                Valores originais:{" "}
                {selectedValues.length > 0
                  ? getSelectedLabels(selectedValues)
                  : "Nenhum valor selecionado"}
              </Typography>
              <FormControl fullWidth size="small">
                <InputLabel id={`select-label-${field._id}`}>
                  {field.name}
                </InputLabel>
                <Select
                  labelId={`select-label-${field._id}`}
                  multiple
                  value={selectedEditedValues}
                  onChange={(e) => handleValueChange(field._id, e.target.value)}
                  label={field.name}
                >
                  {field.options?.map((opt: any) => (
                    <MenuItem key={opt.value} value={opt.value}>
                      {opt.key}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              {selectedEditedValues.length > 0 && (
                <Typography
                  variant="caption"
                  color="primary"
                  sx={{ mt: 1, display: "block" }}
                >
                  Novos valores: {getSelectedLabels(selectedEditedValues)}
                </Typography>
              )}
            </Box>
          );
        }

        return (
          <Box>
            <Typography color="text.secondary" sx={{ mb: 1 }}>
              Valor original: {currentOption?.key || "Nenhum valor selecionado"}
            </Typography>
            <FormControl fullWidth size="small">
              <InputLabel id={`select-label-${field._id}`}>
                {field.name}
              </InputLabel>
              <Select
                labelId={`select-label-${field._id}`}
                value={editedValue !== undefined ? editedValue : data || ""}
                onChange={(e) => handleValueChange(field._id, e.target.value)}
                label={field.name}
              >
                <MenuItem value="">
                  <em>Selecione uma opção</em>
                </MenuItem>
                {field.options?.map((opt: any) => (
                  <MenuItem key={opt.value} value={opt.value}>
                    {opt.key}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            {editedOption && (
              <Typography
                variant="caption"
                color="primary"
                sx={{ mt: 1, display: "block" }}
              >
                Novo valor: {editedOption.key}
              </Typography>
            )}
          </Box>
        );

      case PhaseFormFieldType.NUMBER:
        const numberData = data?.value || data;
        const currentNumber =
          editedValue !== undefined ? editedValue : numberData;

        const formatNumber = (value: any) => {
          if (value === null || value === undefined || value === "")
            return "Nenhum valor";
          const numValue =
            typeof value === "string" ? parseFloat(value) : value;
          return numValue.toLocaleString("pt-BR");
        };

        return (
          <Box>
            <Typography color="text.secondary" sx={{ mb: 1 }}>
              Valor original: {formatNumber(numberData)}
            </Typography>
            <TextField
              type="number"
              value={currentNumber || ""}
              onChange={(e) => handleValueChange(field._id, e.target.value)}
              fullWidth
              size="small"
              variant="outlined"
            />
            {editedValue && (
              <Typography
                variant="caption"
                color="primary"
                sx={{ mt: 1, display: "block" }}
              >
                Novo valor: {formatNumber(editedValue)}
              </Typography>
            )}
          </Box>
        );

      case PhaseFormFieldType.TEXT:
      case PhaseFormFieldType.STRING:
        const textData = data?.value || data;
        const currentText = editedValue !== undefined ? editedValue : textData;

        const formatText = (value: any) => {
          if (value === null || value === undefined || value === "")
            return "Nenhum texto";
          return String(value);
        };

        return (
          <Box>
            <Typography color="text.secondary" sx={{ mb: 1 }}>
              Valor original: {formatText(textData)}
            </Typography>
            <TextField
              multiline={field.type === PhaseFormFieldType.TEXT}
              rows={field.type === PhaseFormFieldType.TEXT ? 4 : 1}
              value={currentText || ""}
              onChange={(e) => handleValueChange(field._id, e.target.value)}
              fullWidth
              size="small"
              variant="outlined"
            />
            {editedValue && (
              <Typography
                variant="caption"
                color="primary"
                sx={{ mt: 1, display: "block" }}
              >
                Novo valor: {formatText(editedValue)}
              </Typography>
            )}
          </Box>
        );

      case PhaseFormFieldType.UPLOAD:
        const filesData = editedValue !== undefined ? editedValue : data || [];
        const filesArray = Array.isArray(filesData)
          ? filesData
          : filesData
          ? [filesData]
          : [];

        const uploadFiles: UploadFile[] = filesArray.map((f: any) => ({
          id: f.id || Math.random().toString(36).substring(2, 11),
          fileName: f.fileName,
          fileSize: f.fileSize,
          path: f.path,
          progress: f.progress !== undefined ? f.progress : 100,
          status: f.status || "completed",
          file: undefined,
        }));

        const phaseFormId =
          (filledForm as any).phaseFormId ||
          (typeof filledForm.phaseForm === "object" &&
          filledForm.phaseForm !== null
            ? (filledForm.phaseForm as any)._id
            : filledForm.phaseForm);

        const processId =
          filledForm.processId ||
          (typeof (filledForm as any).process === "object" &&
          (filledForm as any).process !== null
            ? (filledForm as any).process._id
            : (filledForm as any).process);

        return (
          <Box>
            <Typography color="text.secondary" sx={{ mb: 1 }}>
              Arquivos:
            </Typography>
            <UploadField
              value={uploadFiles}
              onChange={(newFiles) => handleValueChange(field._id, newFiles)}
              label={field.name}
              description={field.description}
              required={false}
              multiline={field.multiline}
              processId={processId}
              phaseFormId={phaseFormId}
              formFieldId={field._id}
              transparent={true}
            />
          </Box>
        );

      case PhaseFormFieldType.INVOICE:
        if (!data) {
          return (
            <Typography color="text.secondary">
              Nenhum arquivo enviado
            </Typography>
          );
        }

        if (Array.isArray(data)) {
          return (
            <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
              {data.map((file: any) => renderFileDownload(file))}
            </Box>
          );
        }
        return (
          <Typography color="text.secondary">Nenhum arquivo enviado</Typography>
        );

      case PhaseFormFieldType.PAGE:
        return null;

      case PhaseFormFieldType.ASSET:
      case PhaseFormFieldType.ROLE:
        // Para outros tipos, usar a função utilitária para renderizar corretamente
        // Verificar se os dados estão aninhados em uma propriedade 'value'
        const fieldData = data?.value || data;

        return (
          <Box>
            <Typography color="text.secondary" sx={{ mb: 1 }}>
              Valor original:
            </Typography>
            {renderDataValue(fieldData, field.name)}
          </Box>
        );

      case PhaseFormFieldType.DATE:
        // Verificar se os dados estão aninhados em uma propriedade 'value'
        const dateData = data?.value || data;
        const currentDate = editedValue !== undefined ? editedValue : dateData;

        return (
          <Box>
            <Typography color="text.secondary" sx={{ mb: 1 }}>
              Valor original:{" "}
              {dateData
                ? formatDateTime(dateData).date
                : "Nenhuma data definida"}
            </Typography>
            <TextField
              type="date"
              value={
                currentDate
                  ? new Date(currentDate).toISOString().split("T")[0]
                  : ""
              }
              onChange={(e) => handleValueChange(field._id, e.target.value)}
              fullWidth
              size="small"
              variant="outlined"
              InputLabelProps={{
                shrink: true,
              }}
            />
            {editedValue && (
              <Typography
                variant="caption"
                color="primary"
                sx={{ mt: 1, display: "block" }}
              >
                Nova data: {formatDateTime(editedValue).date}
              </Typography>
            )}
          </Box>
        );

      case PhaseFormFieldType.MONEY:
        // Verificar se os dados estão aninhados em uma propriedade 'value'
        const moneyData = data?.value || data;
        const currentMoney =
          editedValue !== undefined ? editedValue : moneyData;

        const formatMoney = (value: any) => {
          if (!value) return "R$ 0,00";
          const numValue =
            typeof value === "string" ? parseFloat(value) : value;
          return `R$ ${numValue.toLocaleString("pt-BR", {
            minimumFractionDigits: 2,
            maximumFractionDigits: 2,
          })}`;
        };

        return (
          <Box>
            <Typography color="text.secondary" sx={{ mb: 1 }}>
              Valor original: {formatMoney(moneyData)}
            </Typography>
            <TextField
              type="number"
              value={currentMoney || ""}
              onChange={(e) => handleValueChange(field._id, e.target.value)}
              fullWidth
              size="small"
              variant="outlined"
              InputProps={{
                startAdornment: <Typography sx={{ mr: 1 }}>R$</Typography>,
              }}
            />
            {editedValue && (
              <Typography
                variant="caption"
                color="primary"
                sx={{ mt: 1, display: "block" }}
              >
                Novo valor: {formatMoney(editedValue)}
              </Typography>
            )}
          </Box>
        );
      case PhaseFormFieldType.BINDING_QUESTION:
        // Para qualquer tipo não tratado especificamente, usar a função utilitária
        // Verificar se os dados estão aninhados em uma propriedade 'value'
        const selectedValues = Array.isArray(data) ? data : data ? [data] : [];
        const selectedEditedValues = [
          ...selectedValues,
          ...(Array.isArray(editedValue)
            ? editedValue
            : editedValue
            ? [editedValue]
            : []),
        ].filter(onlyUnique);
        console.log(selectedValues, selectedEditedValues);
        const getSelectedLabels = (values: any[]) => {
          return values
            .map((value) => {
              const option = products?.find((opt: any) => opt.value === value);
              return option?.key || value;
            })
            .join(", ");
        };

        return (
          <Box>
            <Typography color="text.secondary" sx={{ mb: 1 }}>
              Valores originais:{" "}
              {selectedValues.length > 0
                ? getSelectedLabels(selectedValues)
                : "Nenhum valor selecionado"}
            </Typography>
            <FormControl fullWidth size="small">
              <InputLabel id={`select-label-${field._id}`}>
                {field.name}
              </InputLabel>
              <Select
                labelId={`select-label-${field._id}`}
                multiple
                value={selectedEditedValues}
                onChange={(e) =>
                  handleValueChange(field._id, e.target.value, field.type)
                }
                label={field.name}
              >
                {products?.map((opt: any) => (
                  <MenuItem key={opt.value} value={opt.value}>
                    {opt.key}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            {selectedEditedValues.length > 0 && (
              <Typography
                variant="caption"
                color="primary"
                sx={{ mt: 1, display: "block" }}
              >
                Novos valores: {getSelectedLabels(selectedEditedValues)}
              </Typography>
            )}
          </Box>
        );

      default:
        // Para qualquer tipo não tratado especificamente, usar a função utilitária
        // Verificar se os dados estão aninhados em uma propriedade 'value'
        const defaultData = data?.value || data;

        return (
          <Box>
            <Typography color="text.secondary" sx={{ mb: 1 }}>
              Valor original:
            </Typography>
            {renderDataValue(defaultData, field.name)}
          </Box>
        );
    }
  };

  // Função para formatar data e hora
  const formatDateTime = (dateString: any): { date: string; time: string } => {
    if (!dateString) return { date: "N/A", time: "N/A" };

    try {
      // Convert to string if it's not already
      const dateStr = String(dateString);
      const date = new Date(dateStr);

      // Check if the date is valid
      if (isNaN(date.getTime())) {
        return { date: "Data inválida", time: "Hora inválida" };
      }

      // Formatação da data: DD/MM/YYYY
      const formattedDate = date.toLocaleDateString("pt-BR", {
        day: "2-digit",
        month: "2-digit",
        year: "numeric",
      });

      // Formatação da hora: HH:MM
      const formattedTime = date.toLocaleTimeString("pt-BR", {
        hour: "2-digit",
        minute: "2-digit",
        hour12: false,
      });

      return { date: formattedDate, time: formattedTime };
    } catch (error) {
      console.error("Erro ao formatar data:", error);
      return { date: "Data inválida", time: "Hora inválida" };
    }
  };

  // Componente para exibir dados de agendamento
  const AppointmentScheduleInfo = ({
    appointmentSchedule,
  }: {
    appointmentSchedule: any;
  }) => {
    if (!appointmentSchedule) return null;

    const { date: formattedDate, time: formattedTime } = formatDateTime(
      appointmentSchedule?.date
    );

    // Mapeamento de status para texto em português e cor
    const statusMap: Record<string, { label: string; color: string }> = {
      SCHEDULED: { label: "Agendado", color: "success" },
      COMPLETED: { label: "Concluído", color: "primary" },
      CANCELED: { label: "Cancelado", color: "error" },
      PENDING: { label: "Pendente", color: "warning" },
    };

    const statusInfo = statusMap[appointmentSchedule.status] || {
      label: appointmentSchedule.status,
      color: "default",
    };

    return (
      <Box
        sx={{
          mb: 3,
          p: 3,
          bgcolor: "background.default",
          borderRadius: 2,
          border: "1px solid",
          borderColor: "primary.light",
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
          <Typography
            variant="h6"
            sx={{ color: "primary.main", fontWeight: "bold", flexGrow: 1 }}
          >
            Agendamento
          </Typography>
          <Chip
            label={statusInfo.label}
            color={statusInfo.color as any}
            size="small"
          />
        </Box>

        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" },
            gap: 2,
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <EventIcon color="primary" fontSize="small" />
            <Typography>
              <strong>Data:</strong> {formattedDate}
            </Typography>
          </Box>

          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <AccessTimeIcon color="primary" fontSize="small" />
            <Typography>
              <strong>Horário:</strong> {formattedTime}
            </Typography>
          </Box>

          {appointmentSchedule.clientName && (
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <PersonIcon color="primary" fontSize="small" />
              <Typography>
                <strong>Cliente:</strong> {appointmentSchedule.clientName}
              </Typography>
            </Box>
          )}

          {appointmentSchedule.clientEmail && (
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <EmailIcon color="primary" fontSize="small" />
              <Typography>
                <strong>Email:</strong> {appointmentSchedule.clientEmail}
              </Typography>
            </Box>
          )}

          {appointmentSchedule.clientPhone && (
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <PhoneIcon color="primary" fontSize="small" />
              <Typography>
                <strong>Telefone:</strong> {appointmentSchedule.clientPhone}
              </Typography>
            </Box>
          )}
        </Box>

        {appointmentSchedule.notes && (
          <Box
            sx={{ mt: 2, display: "flex", alignItems: "flex-start", gap: 1 }}
          >
            <NoteIcon color="primary" fontSize="small" sx={{ mt: 0.5 }} />
            <Typography>
              <strong>Observações:</strong>
              <br />
              {appointmentSchedule.notes}
            </Typography>
          </Box>
        )}
      </Box>
    );
  };
  console.log("filledform", filledForm.docs);
  return (
    <Box sx={{ p: 3, width: "100%", mx: "auto" }}>
      <Paper sx={{ p: 3, mb: 3, borderRadius: 2 }}>
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            mb: hideHeader ? 1 : 3,
          }}
        >
          {!hideHeader && (
            <Typography
              variant="h5"
              sx={{ color: "primary.main", fontWeight: "medium" }}
            >
              Visualizar Formulário Preenchido
            </Typography>
          )}
          {Object.keys(editedValues).length > 0 && (
            <Button
              variant="contained"
              color="primary"
              onClick={handleSave}
              disabled={isSaving}
              sx={{ ml: "auto" }}
            >
              {isSaving ? "Salvando..." : "Salvar Alterações"}
            </Button>
          )}
        </Box>

        {!hideHeader && (
          <Box
            sx={{ mb: 3, p: 2, bgcolor: "background.default", borderRadius: 1 }}
          >
            <Typography>
              <strong>ID:</strong> {filledForm?._id || "N/A"}
            </Typography>
            <Typography>
              <strong>Processo:</strong> {filledForm?.processId || "N/A"}
            </Typography>
            <Typography>
              <strong>Status:</strong> {filledForm?.status || "N/A"}
            </Typography>
            <Typography>
              <strong>Data de criação:</strong>{" "}
              {String(filledForm?.createdAt || "N/A")}
            </Typography>
          </Box>
        )}

        {/* Exibir informações de agendamento quando disponíveis */}
        {!hideHeader && filledForm?.appointmentSchedule && (
          <>
            <Divider sx={{ my: 3 }} />
            <AppointmentScheduleInfo
              appointmentSchedule={filledForm.appointmentSchedule}
            />
          </>
        )}

        {!hideHeader && <Divider sx={{ my: 3 }} />}

        {!hideHeader && (
          <Typography variant="h6" gutterBottom sx={{ color: "primary.main" }}>
            Respostas
          </Typography>
        )}

        {filledForm?.data &&
          filledForm.data.map((item: any, index: number) => {
            // Ignorar campos do tipo PAGE
            if (item?.field?.type === PhaseFormFieldType.PAGE) return null;
            console.log(item);

            return (
              <Box
                key={item?.field?._id || index}
                sx={{
                  mb: 4,
                  p: 2,
                  bgcolor: "background.default",
                  borderRadius: 1,
                }}
              >
                <Typography
                  variant="subtitle1"
                  fontWeight="bold"
                  color="text.primary"
                >
                  {item?.field?.name || "Campo sem nome"}
                </Typography>

                {item?.field?.description && (
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{ mb: 2, fontStyle: "italic" }}
                  >
                    {item.field.description}
                  </Typography>
                )}

                <Box
                  sx={{
                    mt: 2,
                    pl: 2,
                    borderLeft: "3px solid",
                    borderColor: "primary.light",
                  }}
                >
                  {renderFieldValue(item?.field, item?.data)}
                </Box>

                {index < (filledForm.data?.length || 0) - 1 && (
                  <Divider sx={{ mt: 3 }} />
                )}
              </Box>
            );
          })}
        {filledForm?.docs?.length > 0 && (
          <>
            <Divider sx={{ mt: 3 }} />

            <Box
              sx={{
                mt: 4,
                mb: 4,
                p: 2,
                bgcolor: "background.default",
                borderRadius: 1,
              }}
            >
              <Typography
                variant="subtitle1"
                fontWeight="bold"
                color="text.primary"
              >
                {"Documentos"}
              </Typography>
              <Box
                sx={{
                  mt: 2,
                  pl: 2,
                  borderLeft: "3px solid",
                  borderColor: "primary.light",
                }}
              >
                {filledForm?.docs?.map((item2, id2) => renderDoc(item2))}
              </Box>
            </Box>
          </>
        )}
      </Paper>
    </Box>
  );
};

export default FilledFormForm;
